// Chemin: C:\gestion-copro\src\pages\vigie\DocumentDetailVigiePage.tsx
// Détail document VIGIE — Vue lecture avec métadonnées et téléchargement
// Session 100 : Placeholder
// Session 102 : Version fonctionnelle
// Session 123 : Ajout modification (titre, type, catégorie, description, date) + suppression avec double confirmation
// Session 125 : Masquer catégorie "Interne CS" dans le dropdown édition
// Session 133 : Encadré statut paiement pour appels de fonds et factures
//               Affiche réglé (vert) ou non réglé (rouge) avec détails du paiement
//               Permet de marquer comme réglé ou modifier un paiement existant
// Session 144 : Bloc "Correspondance liee" — affiche rattachement courrier d'origine,
// Session 272 : Bloc "Courrier lie" — charge le courrier Firestore via documentGedId, statut + bouton Gerer
//               évaluation qualité (complète/partielle/incomplète), points traités/manquants,
//               lien cliquable vers le courrier dans "Mes correspondances"

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getTypeDocumentLabel,
  getTypeColor,
  getCategorieLabel,
  TYPES_DOCUMENT,
  deleteDocument
} from '../../services/document.service';
import { CATEGORIES_DOCUMENT_OPTIONS } from '../../constants/documentTaxonomy';
import { TypeDocumentGED, CategorieDocument } from '../../types/document.types';
import {
  ArrowLeft,
  FileText,
  Download,
  Calendar,
  User,
  FolderOpen,
  Tag,
  Loader2,
  AlertCircle,
  ExternalLink,
  Pencil,
  Trash2,
  X,
  Save,
  CheckCircle,
  CreditCard,
  Banknote,
  Check,
  Link2,
  Eye,
  Send
} from 'lucide-react';

// Session 125 : Catégories visibles côté VIGIE (sans "Interne CS")
const VIGIE_CATEGORIES_OPTIONS = CATEGORIES_DOCUMENT_OPTIONS.filter(
  cat => cat.value !== 'interne_cs'
);

// Session 133 : Types de documents financiers
const TYPES_FINANCIERS = ['appel_fonds', 'facture'];

// ============================================
// UTILITAIRES
// ============================================

const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'Non renseigné';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return 'Non renseigné';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 o';
  const k = 1024;
  const sizes = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Convertir un Firestore Timestamp en string YYYY-MM-DD pour input date
const timestampToInputDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

/** Session 133 : Label mode de paiement */
const getModePaiementLabel = (mode: string): string => {
  const labels: Record<string, string> = {
    virement: 'Virement bancaire',
    cheque: 'Chèque',
    prelevement: 'Prélèvement automatique',
    especes: 'Espèces'
  };
  return labels[mode] || mode;
};

/** Session 133 : Formater une date YYYY-MM-DD en français */
const formatDateStr = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const DocumentDetailVigiePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [docData, setDocData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Session 123 : États modification
  const [isEditing, setIsEditing] = useState(false);
  const [editTitre, setEditTitre] = useState('');
  const [editType, setEditType] = useState<TypeDocumentGED>('autre');
  const [editCategorie, setEditCategorie] = useState<CategorieDocument>('administratif');
  const [editDescription, setEditDescription] = useState('');
  const [editDateDocument, setEditDateDocument] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Session 123 : États suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Message succes
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Session 272 : Courrier lie — charge via documentGedId si source sentinelle_vigie
  const [courrierLie, setCourrierLie] = useState<any>(null);

  // Session 133 : États paiement
  const [showPaiementForm, setShowPaiementForm] = useState(false);
  const [paiementDate, setPaiementDate] = useState(new Date().toISOString().split('T')[0]);
  const [paiementMode, setPaiementMode] = useState<string>('');
  const [paiementReference, setPaiementReference] = useState('');
  const [paiementSaving, setPaiementSaving] = useState(false);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    const charger = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const docRef = doc(db, 'documents', id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          setError('Document non trouvé');
          return;
        }
        setDocData({ id: docSnap.id, ...docSnap.data() });
      } catch (err) {
        console.error('Erreur:', err);
        setError('Impossible de charger le document');
      } finally {
        setLoading(false);
      }
    };
    charger();
  }, [id]);

  // Session 272 : Charger le courrier lie via courrierId stocke sur le document GED
  // getDoc direct — pas de query filtree (bloquee par regles Firestore VIGIE)
  useEffect(() => {
    if (!docData?.courrierId) return;
    const chargerCourrierLie = async () => {
      try {
        const snap = await getDoc(doc(db, 'courriers', docData.courrierId));
        if (snap.exists()) {
          setCourrierLie({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.warn('[DocumentDetailVigiePage] Erreur chargement courrier lie:', err);
      }
    };
    chargerCourrierLie();
  }, [docData?.courrierId]);

  // Session 123 : Initialiser les champs d'édition
  // Session 286 : Interception — si document courrier lié en brouillon → rediriger vers CourrierFormVigiePage
  //               Garantit que toute modification passe par le flux courrier (SCUD, modifieParUtilisateur)
  const startEditing = () => {
    if (!docData) return;
    // Si le document est un courrier/email lié à un brouillon → éditer via le formulaire courrier
    if (docData.courrierId && courrierLie && (courrierLie as any).statut === 'brouillon') {
      navigate(`/vigie/courriers/${docData.courrierId}/modifier`);
      return;
    }
    setEditTitre(docData.titre || '');
    setEditType((docData.type as TypeDocumentGED) || 'autre');
    setEditCategorie((docData.categorie as CategorieDocument) || 'administratif');
    setEditDescription(docData.description || '');
    setEditDateDocument(timestampToInputDate(docData.dateDocument));
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // Session 123 : Sauvegarder les modifications
  const handleSave = async () => {
    if (!id || !editTitre.trim()) {
      setSaveError('Le titre est obligatoire.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const docRef = doc(db, 'documents', id);
      const updates: Record<string, any> = {
        titre: editTitre.trim(),
        type: editType,
        categorie: editCategorie,
        description: editDescription.trim() || null,
        updatedAt: serverTimestamp()
      };

      // Mettre à jour la date du document si modifiée
      if (editDateDocument) {
        updates.dateDocument = Timestamp.fromDate(new Date(editDateDocument));
      }

      await updateDoc(docRef, updates);

      // Mettre à jour l'état local
      setDocData((prev: any) => ({
        ...prev,
        titre: editTitre.trim(),
        type: editType,
        categorie: editCategorie,
        description: editDescription.trim() || null,
        dateDocument: editDateDocument ? { toDate: () => new Date(editDateDocument) } : prev.dateDocument
      }));

      setIsEditing(false);
      setSuccessMessage('Document mis à jour');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setSaveError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Session 123 : Supprimer le document
  const handleDelete = async () => {
    if (!docData) return;
    setDeleting(true);
    try {
      await deleteDocument(docData.id, docData.fichierUrl);
      navigate('/vigie/documents');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Erreur lors de la suppression');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Session 133 : Enregistrer ou modifier un paiement
  const handleSavePaiement = async () => {
    if (!id || !paiementMode || !paiementDate) return;
    setPaiementSaving(true);
    try {
      const docRef = doc(db, 'documents', id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return;

      const existingDonneesCles = (docSnap.data().donneesCles || {}) as Record<string, string>;

      const updatedDonneesCles = {
        ...existingDonneesCles,
        statutPaiement: 'oui',
        datePaiement: paiementDate,
        modePaiement: paiementMode,
        referencePaiement: paiementReference || ''
      };

      await updateDoc(docRef, {
        donneesCles: updatedDonneesCles,
        updatedAt: Timestamp.now()
      });

      // Mettre à jour l'état local
      setDocData((prev: any) => ({
        ...prev,
        donneesCles: updatedDonneesCles
      }));

      setShowPaiementForm(false);
      setSuccessMessage('Paiement enregistré');
    } catch (err) {
      console.error('Erreur enregistrement paiement:', err);
    } finally {
      setPaiementSaving(false);
    }
  };

  // Session 133 : Annuler un paiement (remettre en impayé)
  const handleAnnulerPaiement = async () => {
    if (!id) return;
    setPaiementSaving(true);
    try {
      const docRef = doc(db, 'documents', id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return;

      const existingDonneesCles = (docSnap.data().donneesCles || {}) as Record<string, string>;

      const updatedDonneesCles = {
        ...existingDonneesCles,
        statutPaiement: 'non',
        datePaiement: '',
        modePaiement: '',
        referencePaiement: ''
      };

      await updateDoc(docRef, {
        donneesCles: updatedDonneesCles,
        updatedAt: Timestamp.now()
      });

      setDocData((prev: any) => ({
        ...prev,
        donneesCles: updatedDonneesCles
      }));

      setSuccessMessage('Paiement annulé');
    } catch (err) {
      console.error('Erreur annulation paiement:', err);
    } finally {
      setPaiementSaving(false);
    }
  };

  // Session 133 : Ouvrir le formulaire de modification avec les données existantes
  const startEditPaiement = () => {
    const dc = docData?.donneesCles || {};
    setPaiementDate(dc.datePaiement || new Date().toISOString().split('T')[0]);
    setPaiementMode(dc.modePaiement || '');
    setPaiementReference(dc.referencePaiement || '');
    setShowPaiementForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error || !docData) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/vigie/documents')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error || 'Document non trouvé'}
        </div>
      </div>
    );
  }

  const isImage = docData.fichierType?.includes('image');
  const isPDF = docData.fichierType?.includes('pdf');

  // Session 133 : Déterminer si c'est un document financier
  const isDocumentFinancier = TYPES_FINANCIERS.includes(docData.type);
  const donneesCles = docData.donneesCles as Record<string, string> | undefined;
  const statutPaiement = donneesCles?.statutPaiement || '';
  const estPaye = statutPaiement === 'oui' || statutPaiement === 'regle';

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Navigation */}
      <button
        onClick={() => navigate('/vigie/documents')}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux documents
      </button>

      {/* Message succès */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {isEditing ? (
          /* ============================================ */
          /* MODE ÉDITION                                */
          /* ============================================ */
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Pencil className="w-5 h-5 text-amber-600" />
                Modifier le document
              </h2>
              <button
                onClick={cancelEditing}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Annuler"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
              <input
                type="text"
                value={editTitre}
                onChange={(e) => setEditTitre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Titre du document"
              />
            </div>

            {/* Type + Catégorie */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as TypeDocumentGED)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {TYPES_DOCUMENT.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select
                  value={editCategorie}
                  onChange={(e) => setEditCategorie(e.target.value as CategorieDocument)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  {/* Session 125 : VIGIE_CATEGORIES_OPTIONS (sans Interne CS) */}
                  {VIGIE_CATEGORIES_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.code} - {c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date du document */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date du document</label>
              <input
                type="date"
                value={editDateDocument}
                onChange={(e) => setEditDateDocument(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Description du document..."
              />
            </div>

            {/* Erreur sauvegarde */}
            {saveError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {saveError}
              </div>
            )}

            {/* Boutons sauvegarde */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={cancelEditing}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editTitre.trim()}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        ) : (
          /* ============================================ */
          /* MODE LECTURE                                 */
          /* ============================================ */
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {docData.type && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(docData.type)}`}>
                    {getTypeDocumentLabel(docData.type)}
                  </span>
                )}
                {docData.categorie && (
                  <span className="text-xs text-gray-500">
                    {getCategorieLabel(docData.categorie)}
                  </span>
                )}
              </div>
              <h1 className="text-xl font-bold text-gray-900">{docData.titre}</h1>
              {docData.description && (
                <p className="text-gray-600 mt-1">{docData.description}</p>
              )}
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={startEditing}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                title="Modifier"
              >
                <Pencil className="w-4 h-4" />
                Modifier
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
              {docData.fichierUrl && (
                <a
                  href={docData.fichierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SESSION 133 : STATUT PAIEMENT               */}
      {/* Visible uniquement pour appel_fonds/facture  */}
      {/* ============================================ */}
      {!isEditing && isDocumentFinancier && (
        <div className={`rounded-xl border-2 overflow-hidden ${
          estPaye
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
        }`}>
          <div className="p-5">
            {estPaye ? (
              /* --- ÉTAT PAYÉ --- */
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">Réglé</p>
                      <p className="text-sm text-green-700">
                        {donneesCles?.datePaiement && (
                          <>Le {formatDateStr(donneesCles.datePaiement)}</>
                        )}
                        {donneesCles?.modePaiement && (
                          <> — {getModePaiementLabel(donneesCles.modePaiement)}</>
                        )}
                        {donneesCles?.referencePaiement && (
                          <> — Réf. {donneesCles.referencePaiement}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={startEditPaiement}
                      className="text-xs text-green-700 hover:text-green-900 underline"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={handleAnnulerPaiement}
                      disabled={paiementSaving}
                      className="text-xs text-red-600 hover:text-red-800 underline"
                    >
                      {paiementSaving ? 'Annulation...' : 'Annuler le paiement'}
                    </button>
                  </div>
                </div>

                {/* Montant si disponible */}
                {donneesCles?.montantAppel && (
                  <div className="mt-3 flex items-center gap-2 p-3 bg-green-100 rounded-lg">
                    <Banknote className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">
                      Montant : {donneesCles.montantAppel}
                    </span>
                    {donneesCles?.periodeCharges && (
                      <span className="text-xs text-green-600 ml-2">
                        ({donneesCles.periodeCharges})
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* --- ÉTAT NON PAYÉ --- */
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-800">Non réglé</p>
                      <p className="text-sm text-red-600">
                        {statutPaiement === 'en_attente' ? 'Paiement en attente' : 'Ce document n\'a pas encore été réglé'}
                      </p>
                    </div>
                  </div>
                  {!showPaiementForm && (
                    <button
                      onClick={() => {
                        setPaiementDate(new Date().toISOString().split('T')[0]);
                        setPaiementMode('');
                        setPaiementReference('');
                        setShowPaiementForm(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      <CreditCard className="w-4 h-4" />
                      Marquer comme réglé
                    </button>
                  )}
                </div>

                {/* Montant si disponible */}
                {donneesCles?.montantAppel && (
                  <div className="mt-3 flex items-center gap-2 p-3 bg-red-100 rounded-lg">
                    <Banknote className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-bold text-red-800">
                      Montant dû : {donneesCles.montantAppel}
                    </span>
                    {donneesCles?.periodeCharges && (
                      <span className="text-xs text-red-600 ml-2">
                        ({donneesCles.periodeCharges})
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Formulaire de paiement (création ou modification) */}
            {showPaiementForm && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {estPaye ? 'Modifier le paiement' : 'Enregistrer le paiement'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Date du paiement</label>
                    <input
                      type="date"
                      value={paiementDate}
                      onChange={(e) => setPaiementDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Mode de paiement</label>
                    <select
                      value={paiementMode}
                      onChange={(e) => setPaiementMode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="virement">Virement bancaire</option>
                      <option value="cheque">Chèque</option>
                      <option value="prelevement">Prélèvement</option>
                      <option value="especes">Espèces</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Référence (optionnel)</label>
                    <input
                      type="text"
                      value={paiementReference}
                      onChange={(e) => setPaiementReference(e.target.value)}
                      placeholder="N° chèque, réf. virement..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => setShowPaiementForm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSavePaiement}
                    disabled={paiementSaving || !paiementMode}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {paiementSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Confirmer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Métadonnées — masquées en mode édition */}
      {!isEditing && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Informations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-500">Fichier</p>
                <p className="font-medium text-gray-900">{docData.fichierNom || '-'}</p>
                <p className="text-gray-400">{formatFileSize(docData.fichierTaille)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-500">Date du document</p>
                <p className="font-medium text-gray-900">{formatDate(docData.dateDocument)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-500">Ajouté le</p>
                <p className="font-medium text-gray-900">{formatDate(docData.createdAt)}</p>
              </div>
            </div>

            {docData.uploadeParNom && (
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-gray-500">Ajouté par</p>
                  <p className="font-medium text-gray-900">{docData.uploadeParNom}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session 144 : Intelligence Correspondance — Rattachement et évaluation qualité */}
      {!isEditing && docData.courrierRef_lie && (
        <div className={`rounded-xl shadow-sm p-5 border ${
          docData.qualiteReponse === 'complete'
            ? 'bg-green-50 border-green-200'
            : docData.qualiteReponse === 'partielle'
            ? 'bg-orange-50 border-orange-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <Link2 className={`w-5 h-5 ${
              docData.qualiteReponse === 'complete' ? 'text-green-600'
              : docData.qualiteReponse === 'partielle' ? 'text-orange-600'
              : 'text-red-600'
            }`} />
            <h2 className="font-semibold text-gray-900">Correspondance liée</h2>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-700">
              {docData.typeCorrespondance === 'reponse' ? 'Réponse à '
                : docData.typeCorrespondance === 'relance' ? 'Relance de '
                : 'Lié à '}
            </span>
            <button
              onClick={() => {
                if (docData.courrierId_lie) {
                  navigate(`/vigie/correspondances/${docData.courrierId_lie}`);
                }
              }}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline"
            >
              {docData.courrierRef_lie}
            </button>
            {docData.qualiteReponse && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                docData.qualiteReponse === 'complete'
                  ? 'bg-green-100 text-green-700'
                  : docData.qualiteReponse === 'partielle'
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {docData.qualiteReponse === 'complete' ? 'Réponse complète'
                  : docData.qualiteReponse === 'partielle' ? 'Réponse partielle'
                  : 'Réponse incomplète'}
              </span>
            )}
          </div>

          {/* Alerte qualité */}
          {docData.alerteQualite && (
            <p className="text-sm text-gray-700 mb-3">{docData.alerteQualite}</p>
          )}

          {/* Points traités */}
          {docData.pointsTraites && docData.pointsTraites.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-medium text-green-700 mb-1">Points traités :</p>
              {docData.pointsTraites.map((p: string, i: number) => (
                <p key={i} className="text-xs text-green-700 ml-3">{'\u2713'} {p}</p>
              ))}
            </div>
          )}

          {/* Points manquants */}
          {docData.pointsManquants && docData.pointsManquants.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-700 mb-1">Points sans réponse :</p>
              {docData.pointsManquants.map((p: string, i: number) => (
                <p key={i} className="text-xs text-red-700 ml-3">{'\u2717'} {p}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Session 272 : Bloc Courrier lie */}
      {!isEditing && courrierLie && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <Send className="w-5 h-5 text-amber-600" />
            <h2 className="font-semibold text-gray-900">Courrier lie</h2>
            {courrierLie.statut === 'brouillon' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                Brouillon
              </span>
            )}
            {courrierLie.statut === 'finalise' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                Pret a envoyer
              </span>
            )}
            {courrierLie.statut === 'envoye' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                Envoye
              </span>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2 text-sm">
              <span className="text-gray-500 w-24 shrink-0">Destinataire</span>
              <span className="font-medium text-gray-900">
                {courrierLie.destinataire?.nom || 'Non renseigne'}
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <span className="text-gray-500 w-24 shrink-0">Direction</span>
              <span className="font-medium text-gray-900">Sortant</span>
            </div>
            {courrierLie.statut === 'envoye' && courrierLie.dateEnvoi && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 w-24 shrink-0">Envoye le</span>
                <span className="font-medium text-gray-900">
                  {(() => {
                    const d = courrierLie.dateEnvoi?.toDate ? courrierLie.dateEnvoi.toDate() : new Date(courrierLie.dateEnvoi);
                    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                  })()}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/vigie/courriers/' + courrierLie.id)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
          >
            <Send className="w-4 h-4" />
            Gerer ce courrier
          </button>
        </div>
      )}

      {/* Apercu */}
      {!isEditing && docData.fichierUrl && (isImage || isPDF) && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900">Aperçu</h2>
            <a
              href={docData.fichierUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
            >
              Ouvrir <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          {isImage ? (
            <img
              src={docData.fichierUrl}
              alt={docData.titre}
              className="max-w-full rounded-lg border"
            />
          ) : isPDF ? (
            <iframe
              src={docData.fichierUrl}
              title={docData.titre}
              className="w-full h-96 rounded-lg border"
            />
          ) : null}
        </div>
      )}

      {/* Analyse SENTINELLE si présente */}
      {!isEditing && docData.sentinelle?.analyseEffectuee && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <h2 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">S</span>
            Analyse SENTINELLE
          </h2>

          {/* Session 246 : Badge qualité de lecture */}
          {(document as any).qualiteLecture && (
            <div className="mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-sm text-blue-700 font-medium">Lisibilité du document :</span>
              {(document as any).qualiteLecture === 'bonne' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                  ✓ Bonne
                </span>
              )}
              {(document as any).qualiteLecture === 'moyenne' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  ⚠ Moyenne — l'analyse peut être incomplète
                </span>
              )}
              {(document as any).qualiteLecture === 'faible' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                  ✗ Faible — données extraites à vérifier
                </span>
              )}
            </div>
          )}

          {docData.sentinelle.confidence && (
            <p className="text-sm text-blue-700">
              Confiance : {Math.round(docData.sentinelle.confidence * 100)}%
            </p>
          )}
          {docData.sentinelle.explication && (
            <p className="text-sm text-blue-800 mt-1">{docData.sentinelle.explication}</p>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL SUPPRESSION — Session 123             */}
      {/* ============================================ */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Supprimer ce document ?
              </h3>
              <p className="text-sm font-medium text-gray-900 text-center mb-2 px-4 py-2 bg-gray-100 rounded">
                {docData.titre}
              </p>
              <p className="text-sm text-red-600 text-center mb-6">
                Cette action est irréversible. Le fichier sera définitivement supprimé.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Supprimer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetailVigiePage;
